import React from 'react';
import { 
  Check, 
  X, 
  Sparkles, 
  Crown, 
  Zap, 
  ShieldCheck, 
  ChevronRight, 
  TrendingUp, 
  CreditCard,
  Gift,
  Share2,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip,
  Legend
} from 'recharts';
import { useAuth } from '@/contexts/AuthContext';
import { createPaymentOrder } from '@/db/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const membershipData = [
  { name: '普通用户', value: 4500, color: 'hsl(var(--muted-foreground))' },
  { name: '正式会员', value: 1200, color: 'hsl(var(--primary))' },
  { name: '内测会员', value: 300, color: 'hsl(var(--secondary))' },
];

const benefits = [
  { feature: '每日 AI 对话次数', free: '10次', official: '不限', beta: '不限' },
  { feature: 'AI 图片生成数量', free: '2张', official: '50张/日', beta: '不限' },
  { feature: '文档处理 (PDF/Word)', free: <X className="h-4 w-4 text-muted-foreground" />, official: <Check className="h-4 w-4 text-success" />, beta: <Check className="h-4 w-4 text-success" /> },
  { feature: '实时语音转录', free: <X className="h-4 w-4 text-muted-foreground" />, official: <Check className="h-4 w-4 text-success" />, beta: <Check className="h-4 w-4 text-success" /> },
  { feature: '音视频批量处理', free: <X className="h-4 w-4 text-muted-foreground" />, official: '限量体验', beta: <Check className="h-4 w-4 text-success" /> },
  { feature: '内测功能抢先看', free: <X className="h-4 w-4 text-muted-foreground" />, official: <X className="h-4 w-4 text-muted-foreground" />, beta: <Check className="h-4 w-4 text-success" /> },
  { feature: '专属社群指导', free: <X className="h-4 w-4 text-muted-foreground" />, official: <Check className="h-4 w-4 text-success" />, beta: <Check className="h-4 w-4 text-success" /> },
];

const MembershipPage: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const currentTier = profile?.membership_tier || 'free';
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleUpgrade = async (skuCode: string) => {
    if (!profile) {
      toast.error('请先登录');
      navigate('/login');
      return;
    }

    setIsProcessing(true);
    try {
      const { order_no } = await createPaymentOrder(skuCode);
      toast.success('订单创建成功，正在跳转支付页面...');
      navigate(`/orders/${order_no}`);
    } catch (err: any) {
      const errorMsg = err?.context ? await err.context.text() : err?.message;
      toast.error(`创建订单失败: ${errorMsg || '请检查微信支付配置'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      {/* 英雄区 */}
      <div className="tech-flow-bg h-[240px] md:h-[300px] flex flex-col justify-center px-6 md:px-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-secondary/30 rounded-full blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-primary/30 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto relative z-10">
          <Badge className="bg-white/20 text-white backdrop-blur-md border-none mb-4">
            会员中心
          </Badge>
          <h1 className="text-desktop-h1 max-sm:text-mobile-h1 text-white mb-2 logo-glow">
            赋能您的内容创作
          </h1>
          <p className="text-white/70 max-w-2xl mb-8">
            升级至会员计划，解锁 AI 驱动的创作新动力。无论是高效办公还是自媒体进阶，我们都为您准备了最佳方案。
          </p>

          <div className="flex items-center gap-4 text-white">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-muted flex items-center justify-center overflow-hidden">
                  <img src={`https://i.pravatar.cc/150?u=${i}`} alt="user" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <span className="text-sm font-medium">已有 1,500+ 位创作者加入</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 -mt-16 relative z-20">
        {/* 会员等级卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* 普通用户 */}
          <Card className="rounded-xl border border-border bg-card shadow-card flex flex-col">
            <CardHeader>
              <CardTitle className="text-xl">普通用户</CardTitle>
              <CardDescription>基础功能使用体验</CardDescription>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">¥0</span>
                <span className="text-muted-foreground">/ 永久</span>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <ul className="space-y-3">
                {[
                  "每日 10 次 AI 对话",
                  "基础搜索功能",
                  "浏览历史记录",
                  "社区浏览权限"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full" 
                disabled={currentTier === 'free'} 
                onClick={() => {}}
              >
                {currentTier === 'free' ? '当前等级' : '选择基础版'}
              </Button>
            </CardFooter>
          </Card>

          {/* 正式会员 */}
          <Card className="rounded-xl border-primary bg-card shadow-lg flex flex-col scale-105 relative">
            <div className="absolute top-0 right-0 p-3">
              <Badge className="bg-primary text-white">HOT</Badge>
            </div>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                正式会员
              </CardTitle>
              <CardDescription>为高效创作者量身定制</CardDescription>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-primary">¥29.9</span>
                <span className="text-muted-foreground">/ 月</span>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <ul className="space-y-3">
                {[
                  "无限次 AI 对话次数",
                  "每日 50 张 AI 生图",
                  "完整文档处理能力",
                  "音视频转写与字幕生成",
                  "多风格排版一键应用",
                  "专属社群资源共享"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full bg-primary text-white hover:bg-primary/90 btn-ripple shadow-md"
                disabled={currentTier === 'paid' || isProcessing}
                onClick={() => handleUpgrade('membership_paid')}
              >
                {isProcessing ? '处理中...' : currentTier === 'paid' ? '当前等级' : '立即升级'}
              </Button>
            </CardFooter>
          </Card>

          {/* 内测会员 */}
          <Card className="rounded-xl border-secondary shadow-neon bg-member-bg flex flex-col relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-secondary/20 rounded-full blur-xl" />
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-secondary" />
                内测会员
              </CardTitle>
              <CardDescription>参与共建万象生态</CardDescription>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-secondary">¥99.0</span>
                <span className="text-muted-foreground">/ 永久</span>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <ul className="space-y-3">
                {[
                  "包含正式会员所有权益",
                  "抢先体验所有内测功能",
                  "AI 视频剪辑工具抢先使用",
                  "内测专属徽章与身份标识",
                  "参与核心功能决策讨论",
                  "一对一专家创作指导"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-secondary" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full bg-secondary text-white hover:bg-secondary/90 shadow-md"
                disabled={currentTier === 'beta' || isProcessing}
                onClick={() => handleUpgrade('membership_beta')}
              >
                {isProcessing ? '处理中...' : currentTier === 'beta' ? '当前等级' : '获取邀请码'}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* 权益对比表 */}
        <div className="mb-16">
          <h2 className="text-desktop-h2 max-sm:text-mobile-h2 mb-8 text-center">详细权益对比</h2>
          <div className="rounded-xl border border-border overflow-hidden bg-card">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-[300px]">功能项</TableHead>
                  <TableHead>普通用户</TableHead>
                  <TableHead>正式会员</TableHead>
                  <TableHead>内测会员</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {benefits.map((row) => (
                  <TableRow key={row.feature}>
                    <TableCell className="font-medium">{row.feature}</TableCell>
                    <TableCell>{row.free}</TableCell>
                    <TableCell className="text-primary font-medium">{row.official}</TableCell>
                    <TableCell className="text-secondary font-medium">{row.beta}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* 统计概览 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <h2 className="text-desktop-h2 max-sm:text-mobile-h2">会员生态增长</h2>
            <p className="text-muted-foreground">
              万象聚聚光正快速成长，越来越多的专业创作者选择加入我们的精英社区。通过数据可视化了解我们的会员分布。
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                <div className="text-2xl font-bold text-primary">1.5k</div>
                <div className="text-xs text-muted-foreground">总会员数</div>
              </div>
              <div className="p-4 rounded-xl bg-secondary/5 border border-secondary/10">
                <div className="text-2xl font-bold text-secondary">98%</div>
                <div className="text-xs text-muted-foreground">好评率</div>
              </div>
              <div className="p-4 rounded-xl bg-accent/5 border border-accent/10">
                <div className="text-2xl font-bold text-accent">24h</div>
                <div className="text-xs text-muted-foreground">服务响应</div>
              </div>
            </div>
          </div>
          
          <Card className="rounded-xl p-6 bg-card">
            <CardHeader className="p-0 mb-4">
              <CardTitle className="text-lg">会员占比统计</CardTitle>
            </CardHeader>
            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={membershipData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {membershipData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
